import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import { Loader2, CreditCard, Check, X, Clock, DollarSign, TrendingUp, ShoppingBag, Zap, Sparkles, Heart } from "lucide-react";

const TransactionsView = () => {
  const { authAxios } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await authAxios.get('/transactions');
      setTransactions(response.data);
    } catch (e) {
      // If endpoint doesn't exist, show empty
      console.log("Transactions endpoint may not exist yet");
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate revenue stats
  const getRevenueStats = () => {
    const paidTransactions = transactions.filter(t => t.payment_status === 'paid');
    const totalRevenue = paidTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
    const planRevenue = paidTransactions.filter(t => t.type === 'plan').reduce((sum, t) => sum + (t.amount || 0), 0);
    const merchRevenue = paidTransactions.filter(t => t.type === 'merch').reduce((sum, t) => sum + (t.amount || 0), 0);
    const analyzerRevenue = paidTransactions.filter(t => t.type === 'form_analyzer').reduce((sum, t) => sum + (t.amount || 0), 0);
    const tradingCardRevenue = paidTransactions.filter(t => t.type === 'trading_card').reduce((sum, t) => sum + (t.amount || 0), 0);
    const donationRevenue = paidTransactions.filter(t => t.type === 'donation').reduce((sum, t) => sum + (t.amount || 0), 0);
    
    return { totalRevenue, planRevenue, merchRevenue, analyzerRevenue, tradingCardRevenue, donationRevenue, totalOrders: paidTransactions.length };
  };

  const stats = getRevenueStats();
  
  const filteredTransactions = filter === "all" 
    ? transactions 
    : transactions.filter(t => t.type === filter);

  const getStatusIcon = (status) => {
    switch(status) {
      case 'paid':
      case 'completed':
        return <Check className="w-4 h-4 text-neon-green" />;
      case 'pending':
      case 'initiated':
        return <Clock className="w-4 h-4 text-yellow-400" />;
      default:
        return <X className="w-4 h-4 text-red-500" />;
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Revenue Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <div className="cyber-card p-4 border-green-500/30 bg-gradient-to-br from-green-500/10 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <span className="font-rajdhani text-xs text-gray-400">TOTAL REVENUE</span>
          </div>
          <p className="font-orbitron text-2xl text-green-400">${stats.totalRevenue.toFixed(2)}</p>
        </div>
        
        <div className="cyber-card p-4 border-neon-blue/30 bg-gradient-to-br from-neon-blue/10 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <Zap className="w-5 h-5 text-neon-blue" />
            <span className="font-rajdhani text-xs text-gray-400">PLANS</span>
          </div>
          <p className="font-orbitron text-2xl text-neon-blue">${stats.planRevenue.toFixed(2)}</p>
        </div>
        
        <div className="cyber-card p-4 border-yellow-500/30 bg-gradient-to-br from-yellow-500/10 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <ShoppingBag className="w-5 h-5 text-yellow-400" />
            <span className="font-rajdhani text-xs text-gray-400">MERCHANDISE</span>
          </div>
          <p className="font-orbitron text-2xl text-yellow-400">${stats.merchRevenue.toFixed(2)}</p>
        </div>
        
        <div className="cyber-card p-4 border-neon-pink/30 bg-gradient-to-br from-neon-pink/10 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-5 h-5 text-neon-pink" />
            <span className="font-rajdhani text-xs text-gray-400">AI ANALYZER</span>
          </div>
          <p className="font-orbitron text-2xl text-neon-pink">${stats.analyzerRevenue.toFixed(2)}</p>
        </div>
        
        <div className="cyber-card p-4 border-orange-500/30 bg-gradient-to-br from-orange-500/10 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-orange-400" />
            <span className="font-rajdhani text-xs text-gray-400">TRADING CARDS</span>
          </div>
          <p className="font-orbitron text-2xl text-orange-400">${stats.tradingCardRevenue.toFixed(2)}</p>
        </div>
        
        {/* Donation Earnings Card */}
        <div className="cyber-card p-4 border-red-500/30 bg-gradient-to-br from-red-500/10 via-pink-500/5 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <Heart className="w-5 h-5 text-red-400" />
            <span className="font-rajdhani text-xs text-gray-400">DONATIONS</span>
          </div>
          <p className="font-orbitron text-2xl text-red-400">${stats.donationRevenue.toFixed(2)}</p>
        </div>
        
        <div className="cyber-card p-4 border-purple-500/30 bg-gradient-to-br from-purple-500/10 to-transparent">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard className="w-5 h-5 text-purple-400" />
            <span className="font-rajdhani text-xs text-gray-400">TOTAL ORDERS</span>
          </div>
          <p className="font-orbitron text-2xl text-purple-400">{stats.totalOrders}</p>
        </div>
      </div>

      {/* Header with Filter */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-orbitron text-xl text-white flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-orange-400" />
          Payment Transactions ({filteredTransactions.length})
        </h2>
        
        <div className="flex gap-2 flex-wrap">
          {["all", "plan", "merch", "form_analyzer", "trading_card", "donation"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 font-orbitron text-xs uppercase transition-all ${
                filter === f 
                  ? "bg-gradient-to-r from-neon-pink to-neon-blue text-white" 
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              {f === "form_analyzer" ? "Analyzer" : f === "trading_card" ? "Cards" : f}
            </button>
          ))}
        </div>
      </div>

      {/* Transactions Table */}
      {filteredTransactions.length > 0 ? (
        <div className="cyber-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Date</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Type</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Amount</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Status</th>
                  <th className="text-left p-4 font-orbitron text-xs text-gray-400">Session ID</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(tx => (
                  <tr key={tx.id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-4 font-rajdhani text-sm text-gray-300">
                      {formatDate(tx.created_at)}
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 text-xs font-orbitron uppercase ${
                        tx.type === 'plan' ? 'bg-neon-blue/20 text-neon-blue' : 'bg-neon-pink/20 text-neon-pink'
                      }`}>
                        {tx.type}
                      </span>
                    </td>
                    <td className="p-4 font-orbitron text-sm text-white">
                      ${tx.amount?.toFixed(2) || '0.00'}
                    </td>
                    <td className="p-4">
                      <span className="flex items-center gap-2">
                        {getStatusIcon(tx.payment_status)}
                        <span className="font-rajdhani text-sm text-gray-400 capitalize">
                          {tx.payment_status}
                        </span>
                      </span>
                    </td>
                    <td className="p-4 font-mono text-xs text-gray-500 truncate max-w-[150px]">
                      {tx.session_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 cyber-card">
          <CreditCard className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <p className="font-rajdhani text-gray-500">No transactions yet. Payments will appear here.</p>
        </div>
      )}
    </div>
  );
};

export default TransactionsView;
